import React from "react";
import { CheckCircle2, Loader2, X, XCircle } from "lucide-react";
import { useDiscoveryStore, ActiveDiscoveryRun } from "../../../stores/discoveryStore";

function RunCard({
  run,
  onDismiss,
}: {
  run: ActiveDiscoveryRun;
  onDismiss: () => void;
}) {
  const isRunning = run.status === "RUNNING";
  const isFailed = run.status === "FAILED";
  const isDone = run.status === "SUCCESS";

  React.useEffect(() => {
    if (!isDone) return;
    const timer = setTimeout(onDismiss, 1000);
    return () => clearTimeout(timer);
  }, [isDone, onDismiss]);

  const barColor = isFailed
    ? "bg-red-500"
    : isDone
    ? "bg-emerald-500"
    : "bg-indigo-500";

  return (
    <div className="bg-card border border-border rounded-xl shadow-2xl p-3 w-72 space-y-2.5">
      {/* Header row */}
      <div className="flex items-center gap-2">
        {isRunning ? (
          <Loader2 className="size-4 text-indigo-400 animate-spin shrink-0" />
        ) : isFailed ? (
          <XCircle className="size-4 text-red-400 shrink-0" />
        ) : (
          <CheckCircle2 className="size-4 text-emerald-400 shrink-0" />
        )}
        <span className="flex-1 text-sm font-semibold text-foreground truncate">
          {run.accountName}
        </span>
        {isFailed && (
          <button
            onClick={onDismiss}
            className="p-0.5 hover:bg-muted rounded transition-colors"
            title="Dismiss"
          >
            <X className="size-3.5 text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Progress bar */}
      <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full rounded-full ${barColor} transition-all duration-[400ms] ease-out`}
          style={{ width: `${run.displayedPct}%` }}
        />
      </div>

      {/* Status label */}
      <p className="text-xs">
        {isFailed ? (
          <span className="text-red-400">{run.errorMessage || "Discovery failed"}</span>
        ) : isDone ? (
          <span className="text-emerald-400">
            Complete — {run.totalDiscovered} resources discovered
          </span>
        ) : (
          <span className="text-muted-foreground">
            Discovering…{" "}
            <span className="font-semibold text-foreground">{run.displayedPct}%</span>
            {run.regionsTotal > 0 && (
              <span className="ml-1">
                ({run.regionsCompleted}/{run.regionsTotal} regions)
              </span>
            )}
          </span>
        )}
      </p>
    </div>
  );
}

export function DiscoveryProgressBanner() {
  const { runs, dismiss } = useDiscoveryStore();
  const entries = Object.values(runs);
  if (entries.length === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2">
      {entries.map((run) => (
        <RunCard
          key={run.cloudProviderId}
          run={run}
          onDismiss={() => dismiss(run.cloudProviderId)}
        />
      ))}
    </div>
  );
}
